import type { RequirementDto } from '@/utils/api/membership-service/models/requirement-dto'
import { mapApiListResponse } from '@/utils/api/response-helpers'
import {
  normaliseRequirementType,
  parseRequirementValidation,
  type RequirementValidationPayload,
} from '@/utils/membership/requirements'
import { RequirementType, type Requirement } from '@/utils/membership/types'

import type { CategoryRequirement } from './types'
import type { RequirementFormValues } from './schema'

const DESCRIPTION_KEY = 'description'
export function serialiseValidationPayload(
  payload?: RequirementValidationPayload,
): string | undefined {
  if (!payload) return undefined
  try {
    return JSON.stringify(payload)
  } catch (error) {
    console.warn('Failed to serialise validation payload for requirement:', error)
    return undefined
  }
}

export function parseValidationJson(
  validationJson?: string,
): RequirementValidationPayload | undefined {
  if (!validationJson) return undefined

  try {
    const parsed = JSON.parse(validationJson) as RequirementValidationPayload
    return parsed
  } catch (error) {
    console.warn('Failed to parse validationJson for requirement:', error)
    return undefined
  }
}

export function mapRequirementFromApi(requirementInput: Requirement | RequirementDto | undefined): CategoryRequirement {
  if (!requirementInput || typeof requirementInput !== 'object') {
    return {
      id: undefined,
      label: '',
      mandatory: false,
      type: RequirementType.TEXT,
      validationJson: undefined,
    }
  }

  const requirement = requirementInput as Requirement & RequirementDto & { createdAt?: string; updatedAt?: string }
  const payload = parseRequirementValidation(requirement.validationJson)
  const type = normaliseRequirementType((requirement as { type?: unknown }).type)

  return {
    id: requirement.id,
    label: typeof requirement.label === 'string' ? requirement.label : '',
    mandatory: typeof requirement.mandatory === 'boolean' ? requirement.mandatory : false,
    type,
    description:
      typeof payload?.[DESCRIPTION_KEY] === 'string'
        ? (payload?.[DESCRIPTION_KEY] as string)
        : undefined,
    validationJson: requirement.validationJson,
    createdAt: requirement.createdAt,
    updatedAt: requirement.updatedAt,
  }
}

export function mapRequirementsResponse(payload: unknown): CategoryRequirement[] {
  return mapApiListResponse(payload, (requirement) =>
    mapRequirementFromApi(requirement as Requirement | RequirementDto),
  ).filter((requirement) => Boolean(requirement.label))
}

export function mapRequirementToApi(
  requirement: RequirementFormValues | CategoryRequirement,
): Requirement {
  const existingPayload = parseValidationJson(requirement.validationJson)
  const updatedPayload: RequirementValidationPayload = {
    ...(existingPayload ?? {}),
  }

  if ('description' in requirement) {
    if (requirement.description) {
      updatedPayload[DESCRIPTION_KEY] = requirement.description
    } else if (DESCRIPTION_KEY in updatedPayload) {
      delete updatedPayload[DESCRIPTION_KEY]
    }
  }

  return {
    id: requirement.id,
    label: requirement.label,
    mandatory: requirement.mandatory,
    type: requirement.type ?? RequirementType.TEXT,
    validationJson: serialiseValidationPayload(updatedPayload),
  }
}
